import {
  useCreateChatMutation,
  useGetChatHistoryQuery,
  useLoadMoreChatMutation,
} from '../../services/agrocraftArtisan';
import { showAlert } from '../../reduxActions/alert';
import { useAppDispatch } from '../../storeHook';
import { useEffect, useState } from 'react';
import { TCreateChatRespose } from '../../utils/types/agrocraftArtisan';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  CUSTOM_EVENTS,
  appLogEvent,
} from '../../utils/analytics';
import {
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { updateQueryParams } from '../../utils/functions/common';

export const useApiHelper = () => {
  const [searchParams] = useSearchParams();
  const sessionIdFromSearchParams =
    searchParams.get('sessionId');
  const [sessionId, setSessionIdState] = useState<
    number | undefined
  >(Number(sessionIdFromSearchParams));
  const [createNewChat] = useCreateChatMutation();
  const [loadMoreChat] = useLoadMoreChatMutation();
  const navigate = useNavigate();

  const setSessionId: React.Dispatch<
    React.SetStateAction<number | undefined>
  > = (id) => {
    updateQueryParams({ sessionId: id }, navigate);
    setSessionIdState(id);
  };

  const { data: chatHistory } = useGetChatHistoryQuery(
    (sessionId
      ? {
          sessionId,
        }
      : undefined) ?? skipToken
  );

  const [responseChat, setResponseChat] = useState<
    TCreateChatRespose[]
  >([]);
  const [isLoadingImages, setIsLoadingImages] =
    useState(false);
  const dispatch = useAppDispatch();

  const handleNewSession = (
    prompt: string,
    ratio: string,
    prompt_style: string,
    sessionId?: number
  ) => {
    setIsLoadingImages(true);
    return new Promise((resolve) => {
      const createNewChatPayload = sessionId
        ? { sessionId }
        : {};
      createNewChat({
        ...createNewChatPayload,
        prompt,
        ratio,
        prompt_style,
      })
        .unwrap()
        .then((res) => {
          if (typeof res != 'string') {
            const responseData = sessionId
              ? [
                  {
                    ...res,
                  },
                ]
              : [...responseChat, { ...res }];
            setResponseChat(responseData);
            setSessionId && setSessionId(res.SessionId);
            resolve(true);
          } else {
            dispatch(
              showAlert({
                content: res,
                severity: 'error',
              })
            );
          }
          setIsLoadingImages(false);
        })
        .catch(() => {
          setIsLoadingImages(false);
        });
    });
  };

  const handleLoadMoreChat = (
    chatId: number,
    ratio: string,
    prompt: string,
    prompt_style: string
  ) => {
    setIsLoadingImages(true);
    appLogEvent(CUSTOM_EVENTS.LOAD_MORE_IMAGES, {
      prompt,
      chatId,
      aspectRatio: ratio,
      prompt_style,
    });
    return new Promise((resolve) => {
      loadMoreChat({ chatId, ratio, prompt_style })
        .unwrap()
        .then((res) => {
          if (typeof res != 'string') {
            const responseData = [
              ...responseChat,
              { ...res },
            ];
            setResponseChat(responseData);
            setSessionId && setSessionId(res.SessionId);
            resolve(true);
          } else {
            dispatch(
              showAlert({
                content: res,
                severity: 'error',
              })
            );
          }
          setIsLoadingImages(false);
        })
        .catch(() => {
          setIsLoadingImages(false);
        });
    });
  };

  const downloadCrossOriginImage = async (
    imageSrc: string
  ) => {
    appLogEvent(CUSTOM_EVENTS.DOWNLOAD_IMAGE);
    window.location.href = imageSrc;
  };

  useEffect(() => {
    if (chatHistory?.length) {
      const history = [
        ...chatHistory.map((chat) => ({
          ChatId: chat.ID,
          ChatContents: chat.ChatContents,
          SessionId: chat.ChatSessionId,
          Brand: chat.Brand,
          LoadMore: chat.LoadMore,
          Prompt: chat.Prompt,
        })),
      ];
      setResponseChat([...history].reverse());
    }
  }, [chatHistory]);

  useEffect(() => {
    if (!sessionId) setResponseChat([]);
  }, [sessionId]);

  return {
    isLoadingImages,
    responseChat,
    downloadImage: downloadCrossOriginImage,
    handleNewSession,
    sessionId,
    setSessionId,
    handleLoadMoreChat,
  };
};
